import React, { useEffect, useRef, useState } from "react";
import { adminGetJson, adminPostJson } from "./adminApi";
import { AdminSpinner } from "./AdminLoadingUi";
import { cardStyle, chipStyle, inputStyle, muted, primaryButtonStyle, useElapsed } from "./AdminIntelligenceShared";

// ─────────────────────────────────────────────────────────────────────────────
// Architecture: same POST + poll pattern as Tab 1 & Tab 2.
// Posts accumulate across generations within the same session view.
// ─────────────────────────────────────────────────────────────────────────────

function PostCard({ post }) {
  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 17 }}>{post.headline || post.hook}</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
            {post.surprise != null ? <span style={chipStyle}>Surprise {post.surprise}/10</span> : null}
            {post.content != null ? <span style={chipStyle}>Content {post.content}/10</span> : null}
            {post.lead != null ? <span style={chipStyle}>Lead {post.lead}/10</span> : null}
            {post.format ? <span style={chipStyle}>{post.format}</span> : null}
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gap: 6, fontSize: 14, lineHeight: 1.6 }}>
        {post.hook ? <div><strong>Hook:</strong> {post.hook}</div> : null}
        {post.implication ? <div><strong>Implication:</strong> {post.implication}</div> : null}
        {post.cta ? <div><strong>CTA:</strong> {post.cta}</div> : null}
      </div>
    </div>
  );
}

function parsePosts(propMessages) {
  if (!propMessages?.length) return [];
  // Collect posts from ALL assistant messages, not just the latest.
  const all = [];
  for (const msg of propMessages) {
    if (msg.role !== "assistant") continue;
    // Try raw_data.posts first.
    let posts = null;
    if (msg.raw_data) {
      const raw = typeof msg.raw_data === "string" ? (() => { try { return JSON.parse(msg.raw_data); } catch (_) { return null; } })() : msg.raw_data;
      if (Array.isArray(raw?.posts) && raw.posts.length) posts = raw.posts;
    }
    // Fallback: parse content as JSON array.
    if (!posts && msg.content) {
      try {
        const parsed = JSON.parse(msg.content);
        if (Array.isArray(parsed) && parsed.length) posts = parsed;
      } catch (_) {}
    }
    if (posts) all.push(...posts);
  }
  return all;
}

export default function AdminContentIdeasTab({ sessionId, session, messages: propMessages, schemaOptions, sessionLoading, onSessionStarted, onSessionResolved, onNewSession }) {
  const [guidance, setGuidance] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [count, setCount] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Accumulated posts from current generation session.
  const [localPosts, setLocalPosts] = useState([]);
  const ownGenerateRef = useRef(false);
  const activeGenerateRef = useRef(null);
  const scrollRef = useRef(null);
  const elapsed = useElapsed(submitting);

  const bounds = schemaOptions?.dateBounds?.global || { min: "", max: "" };

  // Derive posts from propMessages when available for this session.
  const messagesReady = Boolean(sessionId && session?.id === sessionId);
  const propPosts = messagesReady ? parsePosts(propMessages) : [];
  const propError = messagesReady && session?.status === "error" ? (session.error_message || "Content generation failed.") : null;

  // Sync from parent when the user navigates to a past session via the sidebar.
  useEffect(() => {
    if (ownGenerateRef.current) {
      ownGenerateRef.current = false;
      return;
    }
    activeGenerateRef.current = null;
    setLocalPosts([]);
    setError("");
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cancel polling on unmount.
  useEffect(() => { return () => { activeGenerateRef.current = null; }; }, []);

  // Combine: propPosts (from sidebar/loaded session) first, then localPosts (newest at bottom).
  // Deduplicate by headline/hook.
  const displayPosts = (() => {
    const merged = localPosts.length ? [...propPosts, ...localPosts] : propPosts;
    const seen = new Set();
    return merged.filter((post) => {
      const key = (post.headline || "") + "||" + (post.hook || "");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();
  const displayError = error || (!submitting && !localPosts.length ? propError : null);
  const showLoadingOverlay = (sessionLoading || (sessionId && !messagesReady)) && !submitting && !localPosts.length;
  const isEmpty = !displayPosts.length && !submitting && !displayError;

  // Scroll to bottom when new posts appear or generation starts.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [localPosts.length, submitting]);

  async function generate() {
    if (submitting) return;
    setSubmitting(true);
    setError("");
    // Do NOT clear localPosts — accumulate across runs.

    let resolvedSessionId = sessionId || null;
    try {
      const postRes = await adminPostJson("/api/admin/intelligence/content-ideas", {
        session_id: sessionId || null,
        guidance,
        date_from: dateFrom,
        date_to: dateTo,
        filters: {},
        count,
      });
      resolvedSessionId = postRes.session_id;

      if (resolvedSessionId !== sessionId) {
        ownGenerateRef.current = true;
        onSessionStarted("tab3", resolvedSessionId);
      }

      activeGenerateRef.current = resolvedSessionId;
      let done = false;
      while (!done) {
        await new Promise((r) => setTimeout(r, 3000));
        if (activeGenerateRef.current !== resolvedSessionId) return;
        try {
          const res = await adminGetJson(
            `/api/admin/intelligence/sessions/${encodeURIComponent(resolvedSessionId)}?advance=false`
          );
          if (activeGenerateRef.current !== resolvedSessionId) return;

          if (res.session?.status === "complete") {
            done = true;
            const newPosts = parsePosts(res.messages || []);
            // Append new posts so they appear at the bottom (conversation order).
            if (newPosts.length) setLocalPosts((prev) => [...prev, ...newPosts]);
            onSessionResolved("tab3", resolvedSessionId);
          } else if (res.session?.status === "error") {
            done = true;
            setError(res.session.error_message || "Content generation failed. Please try again.");
            onSessionResolved("tab3", resolvedSessionId);
          }
        } catch (_pollErr) {}
      }
    } catch (err) {
      setError(err.message || String(err));
      if (resolvedSessionId) onSessionResolved("tab3", resolvedSessionId);
    } finally {
      if (activeGenerateRef.current === resolvedSessionId) activeGenerateRef.current = null;
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}>

      {/* ── Sidebar-navigation loading overlay ── */}
      {showLoadingOverlay ? (
        <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.82)", zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <AdminSpinner loading />
          <div style={{ fontWeight: 700, color: "#334155" }}>Loading content ideas…</div>
        </div>
      ) : null}

      {/* ── Scrollable content area ── */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "8px 8px 0 8px" }}>
        <div style={{ display: "grid", gap: 14, paddingBottom: 8, maxWidth: 960 }}>

          {/* Welcome state */}
          {isEmpty ? (
            <div style={cardStyle}>
              <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 10 }}>Content Ideas</div>
              <div style={{ ...muted, fontSize: 15, lineHeight: 1.8, maxWidth: 760 }}>
                Generate FOMO-driven content angles from India's complete government procurement database — live bids, results, contracts, and CPPP awards.
              </div>
              <div style={{ marginTop: 16, display: "grid", gap: 10, maxWidth: 760 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#334155" }}>How it works</div>
                <div style={{ ...muted, fontSize: 14, lineHeight: 1.7 }}>
                  1. Hit <strong>Generate</strong> — the AI scans the database and produces content ideas with hooks, implications, and CTAs ready for marketing.
                </div>
                <div style={{ ...muted, fontSize: 14, lineHeight: 1.7 }}>
                  2. <strong>Keep clicking Generate</strong> — each run produces fresh ideas. Previous ideas stay visible so you can compare and pick the best ones.
                </div>
                <div style={{ ...muted, fontSize: 14, lineHeight: 1.7 }}>
                  3. Use the <strong>guidance</strong> field to steer — e.g. "healthcare", "MSME trends", "defence pricing". Or leave it blank for a broad scan.
                </div>
                <div style={{ ...muted, fontSize: 14, lineHeight: 1.7 }}>
                  4. The AI remembers past output — it won't repeat the same angles. But if data changes, it can provide updates on the same themes.
                </div>
              </div>
            </div>
          ) : null}

          {/* ── Results: post cards (older first, newer at bottom) ── */}
          {displayPosts.length ? (
            <>
              <div style={{ ...muted, fontWeight: 700, fontSize: 14 }}>{displayPosts.length} content ideas</div>
              {displayPosts.map((post, idx) => (
                <PostCard key={`${post.headline || post.hook || idx}-${idx}`} post={post} />
              ))}
            </>
          ) : null}

          {/* Generating indicator — at bottom so it's visible near the input bar */}
          {submitting ? (
            <div style={{ ...cardStyle, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700 }}>
                <AdminSpinner loading />
                <span>Generating {count} content ideas…</span>
              </div>
              <div style={{ ...muted, marginTop: 6 }}>Scanning procurement data… {elapsed}s</div>
            </div>
          ) : null}

          {/* Error — at bottom */}
          {displayError ? (
            <div style={{ ...cardStyle, color: "#b91c1c", fontWeight: 700 }}>Error: {displayError}</div>
          ) : null}
        </div>
      </div>

      {/* ── Input bar — always pinned at bottom ── */}
      <div style={{ borderTop: "1px solid #e5e7eb", background: "#fff", padding: "6px 10px", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <input
            style={{ ...inputStyle, flex: 1, minHeight: 36, minWidth: 160, fontSize: 13 }}
            placeholder="Guidance — e.g. defence, MSME trends… or blank"
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") generate(); }}
          />
          <input type="date" style={{ ...inputStyle, minHeight: 36, padding: "4px 6px", fontSize: 12, width: 130 }} min={bounds?.min || ""} max={bounds?.max || ""} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="From date" placeholder="From" />
          <input type="date" style={{ ...inputStyle, minHeight: 36, padding: "4px 6px", fontSize: 12, width: 130 }} min={bounds?.min || ""} max={bounds?.max || ""} value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="To date" placeholder="To" />
          <select style={{ ...inputStyle, minHeight: 36, padding: "4px 6px", fontSize: 12, width: 52 }} value={count} onChange={(e) => setCount(Number(e.target.value))} title="Number of ideas">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button
            style={{ ...primaryButtonStyle, minWidth: 80, height: 36, flexShrink: 0, fontSize: 13 }}
            onClick={generate}
            disabled={submitting}
          >
            {submitting ? "Generating…" : `Generate ${count}`}
          </button>
          <button style={{ border: 0, background: "transparent", padding: 0, color: "#2563eb", cursor: "pointer", fontWeight: 700, fontSize: 12, flexShrink: 0, whiteSpace: "nowrap" }} onClick={onNewSession}>+ New</button>
        </div>
      </div>
    </div>
  );
}
